import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Card,
  Gallery,
  Heading,
  type MediaTileList,
  type MediaTileListItem,
  Span,
} from "@icg360/design-system";

import { BaseLayout } from "components/base-layout";
import { Stack } from "components/common/stack";
import { InsuranceRepresentative } from "components/shared/insurance-representative";
import { MainLayout } from "components/shared/main-layout";
import { checkForNaStringValue, downloadImage } from "utils";
import { type KeystoneReferral } from "utils/keystone-api-mapping";

import { type GalleryData } from "..";
import { useInspectionsData } from "../hooks";
import { IneligibleWriteOutEntry } from "./ineligible-write-out-entry";
import styles from "./inspections-ineligible.module.scss";

export const InspectionsIneligible = () => {
  const navigate = useNavigate();
  const [galleryData, setGalleryData] = useState<GalleryData | null>(null);
  const { latestInspection, isLoading: loading } = useInspectionsData();

  const inspectionAssets = latestInspection?.review?.inspectionReviewAssets;
  const statusEnum = latestInspection?.status;

  useEffect(() => {
    if (statusEnum && statusEnum !== 480) {
      navigate("/my/overview");
    }
  }, [navigate, statusEnum]);

  if (loading) {
    return <BaseLayout loading />;
  }

  const referralList = latestInspection?.review?.referrals ?? [];
  const sortedReferralList = referralList.toSorted((referral) => {
    return referral?.category === "Roof" ? -1 : 1;
  });

  const ineligibleReferrals: KeystoneReferral[] = [];
  const otherReferrals: KeystoneReferral[] = [];
  (sortedReferralList ?? []).forEach((referral) => {
    if (referral?.actionStatement === "Risk is ineligible") {
      ineligibleReferrals.push(referral);
    } else if (referral) {
      otherReferrals.push(referral);
    }
  });

  const shownAssets = galleryData?.assetIds;
  if (shownAssets && shownAssets.length > 0) {
    const media: MediaTileList & { fileName: string }[] = shownAssets.map(
      (assetId) => {
        const selectedAsset = inspectionAssets?.find(
          (asset) => asset.id === assetId
        );
        const fileName = selectedAsset?.assetFileName;
        return {
          fileName: fileName ?? `image_${assetId}`,
          id: assetId,
          source: `/api/inspection/image/${assetId}`,
        };
      }
    );

    return (
      <Gallery
        media={media}
        title={galleryData.title}
        subheading={galleryData.subHeading}
        primaryAction="Close"
        onPrimaryAction={() => setGalleryData(null)}
        onClose={() => setGalleryData(null)}
        activeAction="Download"
        onActiveAction={(active: MediaTileListItem & { fileName: string }) => {
          downloadImage(active.source, active.fileName);
        }}
      />
    );
  }

  return (
    <BaseLayout>
      <MainLayout
        topRow={
          <Heading className={styles.mainHeadingDS} size="lg">
            Our inspection findings
          </Heading>
        }
        sidebar={<InsuranceRepresentative />}
      >
        <Card border className={styles.writeOutsCardDS}>
          <Heading size="md" className={styles.headingDS}>
            Your property is ineligible for this policy
          </Heading>
          <Span>
            We may have other carriers that work for your property. Please
            contact your insurance rep to explore your options.
          </Span>
          {ineligibleReferrals.length > 0 ? (
            <Stack gap="xl" className={styles.writeOuts}>
              {ineligibleReferrals.map((referral, idx) => {
                const galleryTitle =
                  checkForNaStringValue(referral?.issueStatement ?? "") ?? "";
                const gallerySubHeading =
                  checkForNaStringValue(referral?.locationDetail ?? "") ?? "";
                return (
                  <IneligibleWriteOutEntry
                    key={idx}
                    referral={referral}
                    primaryIneligible={true}
                    showGallery={() => {
                      if (referral?.inspectionReviewAssetIds) {
                        setGalleryData({
                          assetIds: referral.inspectionReviewAssetIds,
                          title: galleryTitle,
                          subHeading: gallerySubHeading,
                        });
                      }
                    }}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Card>

        {otherReferrals.length > 0 ? (
          <Card border className={styles.writeOutsCardDS}>
            <Heading size="md" className={styles.headingDS}>
              Other concerns found
            </Heading>
            <Span>
              We noticed a few extra issues during our inspection. These
              won&apos;t affect your policy eligibility.
            </Span>
            <Stack gap="xl" className={styles.writeOuts}>
              {otherReferrals.map((referral, idx) => {
                const galleryTitle =
                  checkForNaStringValue(referral?.issueStatement ?? "") ?? "";
                const gallerySubHeading =
                  checkForNaStringValue(referral?.locationDetail ?? "") ?? "";
                return (
                  <IneligibleWriteOutEntry
                    key={idx}
                    referral={referral}
                    primaryIneligible={false}
                    showGallery={() => {
                      if (referral?.inspectionReviewAssetIds) {
                        setGalleryData({
                          assetIds: referral.inspectionReviewAssetIds,
                          title: galleryTitle,
                          subHeading: gallerySubHeading,
                        });
                      }
                    }}
                  />
                );
              })}
            </Stack>
          </Card>
        ) : null}
      </MainLayout>
    </BaseLayout>
  );
};
